import { ClaimTypesCell } from "@/components/features/matters/ClaimTypesCell";
import { DataTableColumnHeader } from "@/components/ui";
import { type ReportsMatterActivity } from "@/lib/types";
import { formatDate } from "@/lib/utils/format";
import { MultiValueCell } from "@/lib/utils/tableUtils";
import { type ColumnDef } from "@tanstack/react-table";

const hourFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const formatWords = (value: string) =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) =>
      word ? `${word[0].toUpperCase()}${word.slice(1).toLowerCase()}` : "",
    )
    .join(" ");

const formatActionLabel = (value: string) => {
  const normalizedValue = value.toLowerCase().split("_").join(" ");
  return formatWords(normalizedValue);
};

const getTotalActionCount = (actions?: Array<Record<string, number>>) =>
  actions?.reduce((sum, item) => {
    return (
      sum +
      Object.values(item).reduce(
        (itemSum, count) =>
          typeof count === "number" && Number.isFinite(count)
            ? itemSum + count
            : itemSum,
        0,
      )
    );
  }, 0) ?? 0;

const getActionSummary = (actions?: Array<Record<string, number>>) => {
  if (!actions || actions.length === 0) {
    return [];
  }

  const totals = new Map<string, number>();
  actions.forEach((item) => {
    Object.entries(item).forEach(([action, count]) => {
      if (typeof count !== "number" || Number.isNaN(count)) return;
      totals.set(action, (totals.get(action) ?? 0) + count);
    });
  });

  return Array.from(totals.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(
      ([action, count]) =>
        `${formatActionLabel(action)} (${count.toLocaleString()})`,
    );
};

export const getMatterActivityReportColumns =
  (): ColumnDef<ReportsMatterActivity>[] => [
    {
      accessorFn: (row) => row.matterActivityReportAggregateMatterTitle ?? "",
      id: "Matter",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) =>
        row.original.matterActivityReportAggregateMatterTitle || "-",
      enableHiding: false,
    },
    {
      accessorKey: "matterActivityReportAggregateDate",
      id: "Activity Date",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) =>
        formatDate(row.original.matterActivityReportAggregateDate) ?? "-",
      sortingFn: "customDateTime",
    },
    {
      accessorFn: (row) => row.matterActivityReportAggregateMatterStatus ?? "",
      id: "Matter Status",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) =>
        row.original.matterActivityReportAggregateMatterStatus || "-",
    },
    {
      accessorFn: (row) =>
        row.matterActivityReportAggregateMatterStatusCategory ?? "",
      id: "Status Category",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) =>
        row.original.matterActivityReportAggregateMatterStatusCategory
          ? formatWords(
              row.original.matterActivityReportAggregateMatterStatusCategory,
            )
          : "-",
    },
    {
      accessorKey: "matterActivityReportAggregateIncidentType",
      id: "Incident Type",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) =>
        row.original.matterActivityReportAggregateIncidentType || "-",
    },
    {
      accessorFn: (row) =>
        (row.claimTypes ?? []).map((claimType) => claimType.label).join(", "),
      id: "Claim Types",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <ClaimTypesCell claimTypes={row.original.claimTypes} />
      ),
    },
    {
      accessorFn: (row) => row.matterActivityReportAggregateMatterTeam ?? [],
      id: "Team",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <MultiValueCell
          value={row.original.matterActivityReportAggregateMatterTeam ?? []}
        />
      ),
      enableSorting: false,
    },
    {
      accessorFn: (row) =>
        row.matterActivityReportAggregateActivityTotalHours ?? 0,
      id: "Activity Hours",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) =>
        hourFormatter.format(
          row.original.matterActivityReportAggregateActivityTotalHours ?? 0,
        ),
    },
    {
      accessorFn: (row) =>
        row.matterActivityReportAggregateTimeline?.length ?? 0,
      id: "Sessions",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) =>
        (
          row.original.matterActivityReportAggregateTimeline?.length ?? 0
        ).toLocaleString(),
    },
    {
      accessorFn: (row) =>
        getTotalActionCount(row.matterActivityReportAggregateActionsCompleted),
      id: "Actions Completed",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <MultiValueCell
          value={getActionSummary(
            row.original.matterActivityReportAggregateActionsCompleted,
          )}
        />
      ),
    },
  ];
